import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import { ValueCardComponent, ValueItem } from './value-card.component';
import { rsFromTo } from '../../shared/motion/rs-motion';

let registered = false;

@Component({
  selector: 'app-values',
  standalone: true,
  imports: [CommonModule, ValueCardComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './values.component.html',
  styleUrls: ['./values.component.scss'],
})
export class ValuesComponent implements AfterViewInit, OnDestroy {
  @ViewChild('root') root?: ElementRef<HTMLElement>;
  @ViewChild('head') head?: ElementRef<HTMLElement>;
  @ViewChild('title') title?: ElementRef<HTMLElement>;
  @ViewChild('grid') grid?: ElementRef<HTMLElement>;
  @ViewChild('bg') bg?: ElementRef<HTMLElement>;
  @ViewChild('motes') motes?: ElementRef<HTMLElement>;
  @ViewChildren('card', { read: ElementRef })
  cardEls?: QueryList<ElementRef<HTMLElement>>;

  /** The one expanded card, or null. */
  readonly open = signal<string | null>(null);

  reduced = false;

  /** Copy is the site's existing wording — not rewritten. */
  readonly items: ValueItem[] = [
    {
      id: 'innovation',
      no: '01',
      title: 'Innovation',
      copy: 'We embrace creativity and continuous learning to build future-ready solutions.',
      tone: 'pink',
    },
    {
      id: 'excellence',
      no: '02',
      title: 'Excellence',
      copy: 'We strive for perfection in everything we do, every single day.',
      tone: 'violet',
    },
    {
      id: 'collaboration',
      no: '03',
      title: 'Collaboration',
      copy: 'We work together as a team to achieve shared goals and success.',
      tone: 'blue',
    },
    {
      id: 'integrity',
      no: '04',
      title: 'Integrity',
      copy: 'We operate with transparency and honesty in all our interactions.',
      tone: 'violet',
    },
    {
      id: 'customer',
      no: '05',
      title: 'Customer-centricity',
      copy: "We prioritize our clients' needs and success above all else.",
      tone: 'pink',
    },
    {
      id: 'agility',
      no: '06',
      title: 'Agility',
      copy: 'We adapt quickly to changing requirements and circumstances.',
      tone: 'blue',
    },
    {
      id: 'accountability',
      no: '07',
      title: 'Accountability',
      copy: 'We take ownership of our work and are responsible for the outcomes.',
      tone: 'violet',
    },
    {
      id: 'improvement',
      no: '08',
      title: 'Continuous Improvement',
      copy: 'We consistently refine our processes to deliver superior IT solutions.',
      tone: 'pink',
    },
  ];

  private ctx?: gsap.Context;
  private split?: SplitText;
  private cleanups: Array<() => void> = [];
  private isBrowser: boolean;

  constructor(
    private zone: NgZone,
    private host: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  trackItem = (_i: number, it: ValueItem) => it.id;

  /** Click to open; click the open one again to close. */
  toggle(id: string): void {
    this.open.set(this.open() === id ? null : id);
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reduced) return;

    if (!registered) {
      gsap.registerPlugin(ScrollTrigger, SplitText, MotionPathPlugin);
      registered = true;
    }

    this.zone.runOutsideAngular(() => {
      this.ctx = gsap.context(() => {
        this.buildHeader();
        this.buildCards();
        this.buildMotes();
        this.buildSparks();
      }, this.host.nativeElement);

      this.bindParallax();
    });
  }

  /* ------------------------------------------------------------------
     HEADER — badge, then the heading resolving word by word
     ------------------------------------------------------------------ */
  private buildHeader(): void {
    const titleEl = this.title?.nativeElement;

    const tl = gsap.timeline({
      defaults: { ease: 'power3.out' },
      scrollTrigger: {
        trigger: this.head!.nativeElement,
        start: 'top 82%',
        once: true,
      },
    });

    tl.fromTo('.values__badge', ...rsFromTo({ opacity: 0, y: 16, duration: 0.5 }));

    if (titleEl) {
      // SplitText gives per-word masking that plain CSS can't do here
      /* Split into words only. The gradient lives on the word itself
         (see the stylesheet) because background-clip:text on a wrapper
         whose text has been re-nested is fragile. */
      this.split = new SplitText(titleEl, {
        type: 'words',
        wordsClass: 'values__word',
      });
      tl.fromTo(
        this.split.words, ...rsFromTo({ opacity: 0, yPercent: 108, duration: 0.72, stagger: 0.08 }),
        '-=0.24'
      );
    } else {
      tl.fromTo(titleEl!, ...rsFromTo({ opacity: 0, y: 24, duration: 0.7 }), '-=0.24');
    }

    tl.fromTo('.values__lede', ...rsFromTo({ opacity: 0, y: 14, duration: 0.55 }), '-=0.4')
      .fromTo('.values__divider', ...rsFromTo({ opacity: 0, scaleX: 0, duration: 0.6 }), '-=0.45');
  }

  /* ------------------------------------------------------------------
     CARDS — left to right, 100ms apart, with a slight rotation
     ------------------------------------------------------------------ */
  private buildCards(): void {
    const cards = (this.cardEls?.toArray() ?? []).map((r) => r.nativeElement);
    if (!cards.length) return;

    gsap.fromTo(cards, ...rsFromTo({
      opacity: 0,
      y: 40,
      scale: 0.95,
      rotate: -1.4,
      duration: 0.78,
      ease: 'power3.out',
      stagger: 0.1,
      scrollTrigger: {
        trigger: this.grid!.nativeElement,
        start: 'top 84%',
        once: true,
      },
    }));
  }

  /* ------------------------------------------------------------------
     AMBIENT — floating motes drifting on their own paths
     ------------------------------------------------------------------ */
  private buildMotes(): void {
    gsap.utils.toArray<HTMLElement>('.values__mote').forEach((m, i) => {
      gsap.to(m, {
        y: i % 2 ? 26 : -30,
        x: i % 3 ? 14 : -12,
        duration: 12 + (i % 5) * 3,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
        delay: -i * 1.4,
      });
      gsap.to(m, {
        opacity: 0.55,
        duration: 4 + (i % 4),
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
        delay: -i * 0.8,
      });
    });
  }

  /* ------------------------------------------------------------------
     MotionPath — the sparks orbiting the Innovation bulb
     ------------------------------------------------------------------ */
  private buildSparks(): void {
    const orbit =
      'M160 92 m -34 0 a 34 26 0 1 0 68 0 a 34 26 0 1 0 -68 0';

    gsap.utils.toArray<SVGElement>('.art__spark').forEach((s, i) => {
      gsap.to(s, {
        motionPath: { path: orbit, alignOrigin: [0.5, 0.5] },
        duration: 7 + i * 1.6,
        ease: 'none',
        repeat: -1,
        delay: -i * 2.2,
      });
    });
  }

  /* ------------------------------------------------------------------
     PARALLAX — cards 4px, background 2px, motes 6px
     ------------------------------------------------------------------ */
  private bindParallax(): void {
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!fine) return;

    const cards = (this.cardEls?.toArray() ?? []).map((r) => r.nativeElement);
    const bg = this.bg?.nativeElement;
    const motes = this.motes?.nativeElement;
    const root = this.root?.nativeElement;
    if (!root) return;

    // quickTo keeps this to one interpolated tween per target
    const cardX = cards.map((c) => gsap.quickTo(c, 'x', { duration: 0.8, ease: 'power3.out' }));
    const cardY = cards.map((c) => gsap.quickTo(c, 'y', { duration: 0.8, ease: 'power3.out' }));
    const bgX = bg ? gsap.quickTo(bg, 'x', { duration: 1.1, ease: 'power3.out' }) : null;
    const bgY = bg ? gsap.quickTo(bg, 'y', { duration: 1.1, ease: 'power3.out' }) : null;
    const moX = motes ? gsap.quickTo(motes, 'x', { duration: 0.9, ease: 'power3.out' }) : null;
    const moY = motes ? gsap.quickTo(motes, 'y', { duration: 0.9, ease: 'power3.out' }) : null;

    let queued = false;
    let nx = 0;
    let ny = 0;

    const apply = () => {
      queued = false;
      cardX.forEach((fn) => fn(nx * 4));
      cardY.forEach((fn) => fn(ny * 4));
      bgX?.(nx * 2);
      bgY?.(ny * 2);
      moX?.(nx * 6);
      moY?.(ny * 6);
    };

    const onMove = (e: MouseEvent) => {
      const r = root.getBoundingClientRect();
      nx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      ny = ((e.clientY - r.top) / r.height - 0.5) * 2;
      if (queued) return;
      queued = true;
      requestAnimationFrame(apply);
    };

    const onLeave = () => {
      nx = 0;
      ny = 0;
      if (queued) return;
      queued = true;
      requestAnimationFrame(apply);
    };

    root.addEventListener('mousemove', onMove, { passive: true });
    root.addEventListener('mouseleave', onLeave, { passive: true });
    this.cleanups.push(() => {
      root.removeEventListener('mousemove', onMove);
      root.removeEventListener('mouseleave', onLeave);
    });
  }

  ngOnDestroy(): void {
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
    this.split?.revert();
    this.ctx?.revert();
  }
}
